import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, inr, dateFmt, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Sparkles, Upload, RefreshCw, Mail, Link2, Unlink, Zap, FileText, ArrowRight, Inbox, GitMerge, AlertTriangle, Check, X as XIcon } from "lucide-react";

const SOURCES = [
  "YouTube", "X (Twitter)", "Instagram", "Substack", "Patreon",
  "Razorpay", "Stripe", "PayPal", "Wise", "Payoneer", "Cashfree", "Instamojo",
  "Bank credit", "Affiliate", "Speaking", "Royalty", "Other",
];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AUD", "SGD", "AED", "CAD"];
const GST_RATES = [0, 5, 12, 18, 28];

const today = () => new Date().toISOString().slice(0, 10);
const empty = () => ({
  date: today(), source: "YouTube", description: "", amount: "",
  currency: "INR", fx_rate: 1, gst_applicable: false, gst_rate: 0,
  is_taxable: true, notes: "",
});

export default function Income() {
  const [items, setItems] = useState([]);
  const [gstSummary, setGstSummary] = useState(null); // { output_gst_invoices, output_gst_incomes, input_gst, net_gst_payable }
  const [filter, setFilter] = useState("all"); // all | invoice | other
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasting, setPasting] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [csvLoading, setCsvLoading] = useState(false);
  const [fxLoading, setFxLoading] = useState(false);
  const [gmail, setGmail] = useState(null); // null=loading, then status object
  const [syncing, setSyncing] = useState(false);
  const [queues, setQueues] = useState({ unclassified: [], dedup_suggestions: [], auto_dedup_active: false });
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchGmail = async () => {
    try {
      const r = await api.get("/gmail/status");
      setGmail(r.data);
    } catch { setGmail({ connected: false }); }
  };
  const fetchQueues = async () => {
    try {
      const r = await api.get("/income/queues");
      setQueues(r.data || { unclassified: [], dedup_suggestions: [] });
    } catch {}
  };
  useEffect(() => { fetchGmail(); fetchQueues(); }, []);

  // Handle redirect-back from Google OAuth
  useEffect(() => {
    const flag = searchParams.get("gmail");
    const err = searchParams.get("error");
    if (flag === "connected=1" || flag === "1" || searchParams.get("connected") === "1") {
      toast.success("Gmail connected — try 'Sync now'");
      setSearchParams({}, { replace: true });
      fetchGmail();
    } else if (err === "state_expired") {
      toast.error("That OAuth session expired — please click Connect Gmail again");
      setSearchParams({}, { replace: true });
    } else if (err === "state_invalid") {
      toast.error("Invalid OAuth state — please sign in and try again");
      setSearchParams({}, { replace: true });
    } else if (err === "oauth_failed") {
      toast.error("Gmail connection failed — check Google Cloud Console redirect URI");
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const connectGmail = async () => {
    try {
      const r = await api.get("/oauth/gmail/login");
      if (r.data?.auth_url) window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start OAuth");
    }
  };

  const syncGmail = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/gmail/sync");
      const { scanned, matched, created, skipped } = r.data || {};
      toast.success(`Synced · ${scanned} scanned · ${matched} payouts · ${created} new · ${skipped} skipped`);
      fetchItems();
      fetchGmail();
      fetchQueues();
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      if (e.response?.status === 401) toast.error("Re-connect Gmail (auth expired)");
      else toast.error(detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const classifyEntry = async (incomeId, source, isBank, createRule) => {
    try {
      await api.patch(`/incomes/${incomeId}/classify`, {
        source, is_bank_credit: isBank, create_rule: createRule,
      });
      toast.success(createRule ? `Tagged & saved rule · all ${source} emails will auto-classify` : `Tagged as ${source}`);
      fetchItems();
      fetchQueues();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const resolveDedup = async (suggestionId, action) => {
    try {
      const r = await api.post(`/incomes/dedup/${suggestionId}/${action}`);
      const count = r.data?.auto_dedup_count;
      if (action === "merge" && count === 3) {
        toast.success("Merged · auto-merge enabled for future duplicates");
      } else if (action === "merge") {
        toast.success(`Merged · ${3 - count} more to enable auto-mode`);
      } else {
        toast.success("Kept both as separate entries");
      }
      fetchItems();
      fetchQueues();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const disconnectGmail = async () => {
    if (!window.confirm("Disconnect Gmail? Your existing imported entries stay.")) return;
    try {
      await api.post("/gmail/disconnect");
      toast.success("Gmail disconnected");
      fetchGmail();
    } catch { toast.error("Disconnect failed"); }
  };

  const fetchGstSummary = async () => {
    try {
      const r = await api.get("/tax/gst-summary");
      setGstSummary(r.data || null);
    } catch { /* ignore */ }
  };
  const fetchItems = async () => {
    const r = await api.get("/income/all");
    setItems(r.data || []);
    fetchGstSummary();
  };
  useEffect(() => { fetchItems(); }, []);

  // Auto-fetch FX rate when currency changes (and not INR)
  useEffect(() => {
    if (form.currency === "INR") {
      if (form.fx_rate !== 1) setForm((f) => ({ ...f, fx_rate: 1 }));
      return;
    }
    let active = true;
    (async () => {
      setFxLoading(true);
      try {
        const r = await api.get(`/fx/rate?from_currency=${form.currency}`);
        if (active) setForm((f) => ({ ...f, fx_rate: Number(r.data.rate.toFixed(4)) }));
      } catch {
        // keep current
      } finally { if (active) setFxLoading(false); }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.currency]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((i) => i.kind === filter);
  }, [items, filter]);

  const totalInr = filtered.reduce((s, i) => s + (i.amount_inr || 0), 0);
  const collectedInr = filtered
    .filter((i) => i.is_collected !== false)
    .reduce((s, i) => s + (i.amount_inr || 0), 0);
  const outstandingInr = Math.max(0, totalInr - collectedInr);
  const taxableInr = filtered
    .filter((i) => i.is_taxable)
    .reduce((s, i) => s + (i.amount_inr_ex_gst ?? i.amount_inr ?? 0), 0);

  // GST stat — liability basis (issuance, not collection). Matches Dashboard.
  // Uses /tax/gst-summary so unpaid issued invoices are included.
  const invoiceOutputGst = gstSummary
    ? (gstSummary.output_cgst || 0) + (gstSummary.output_sgst || 0) + (gstSummary.output_igst || 0)
    : 0;
  const incomeOutputGst = gstSummary?.income_gst || 0;
  const totalOutputGst = gstSummary?.total_output || 0;
  const netGstPayable = gstSummary?.net_payable || 0;
  const incomeGst = filter === "invoice"
    ? invoiceOutputGst
    : filter === "other"
      ? incomeOutputGst
      : netGstPayable;
  const incomeGstSubtitle = gstSummary
    ? (filter === "invoice"
        ? `Output GST on issued invoices`
        : filter === "other"
          ? `Output GST on non-invoice income`
          : `Output ${inr(totalOutputGst)} − ITC ${inr(gstSummary.input_credit || 0)}`)
    : "Loading…";

  const invoiceCount = items.filter((i) => i.kind === "invoice").length;
  const otherCount = items.filter((i) => i.kind === "other").length;

  const bySource = useMemo(() => {
    const map = {};
    filtered.forEach((i) => {
      const v = i.amount_inr_ex_gst ?? i.amount_inr ?? 0;
      map[i.source] = (map[i.source] || 0) + v;
    });
    return Object.entries(map).map(([source, amount]) => ({ source, amount })).sort((a, b) => b.amount - a.amount);
  }, [filtered]);

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (i) => {
    setEditing(i);
    setForm({
      date: i.date, source: SOURCES.includes(i.source) ? i.source : "Other",
      description: i.description || "", amount: i.amount, currency: i.currency,
      fx_rate: i.fx_rate, gst_applicable: !!i.gst_applicable, gst_rate: i.gst_rate || 0,
      is_taxable: i.is_taxable !== false, notes: i.notes || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.amount || !form.source || !form.date) {
      toast.error("Date, source and amount are required");
      return;
    }
    try {
      const body = {
        ...form,
        amount: Number(form.amount),
        fx_rate: Number(form.fx_rate || 1),
        gst_rate: Number(form.gst_rate || 0),
      };
      if (editing) {
        await api.put(`/incomes/${editing.id}`, body);
        toast.success("Income updated");
      } else {
        await api.post("/incomes", body);
        toast.success("Income logged");
      }
      setOpen(false);
      fetchItems();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/incomes/${id}`);
    toast.success("Removed");
    fetchItems();
  };

  const aiParse = async () => {
    if (!pasteText.trim()) { toast.error("Paste an email first"); return; }
    setPasting(true);
    try {
      const r = await api.post("/incomes/parse-email", { text: pasteText });
      if (r.data?.ok && r.data.extracted) {
        const ex = r.data.extracted;
        setForm((f) => ({
          ...f,
          date: ex.date || f.date,
          source: SOURCES.includes(ex.source) ? ex.source : "Other",
          description: ex.description
            ? (ex.reference_id ? `${ex.description} · Ref: ${ex.reference_id}` : ex.description)
            : f.description,
          amount: ex.amount || f.amount,
          currency: ex.currency || f.currency,
        }));
        setPasteOpen(false);
        setPasteText("");
        setOpen(true);
        const extras = [];
        if (ex.gross_amount) extras.push(`gross ₹${Number(ex.gross_amount).toLocaleString("en-IN")}`);
        if (ex.fees) extras.push(`fees ₹${Number(ex.fees).toLocaleString("en-IN")}`);
        if (ex.is_bank_credit) extras.push("bank credit");
        const tail = extras.length ? ` · ${extras.join(" · ")}` : "";
        toast.success(`Parsed via ${ex.parser_name || "AI"}${tail} — review & save`);
      } else {
        toast.error("Couldn't parse — please log manually");
      }
    } catch (e) {
      toast.error("AI parse failed");
    } finally { setPasting(false); }
  };

  const csvImport = async () => {
    if (!csvText.trim()) { toast.error("Paste CSV content"); return; }
    setCsvLoading(true);
    try {
      const r = await api.post("/incomes/csv", { csv: csvText });
      const { imported, errors } = r.data;
      if (imported > 0) toast.success(`Imported ${imported} rows${errors?.length ? `, ${errors.length} errors` : ""}`);
      else toast.error(`No rows imported${errors?.length ? ` (${errors.length} errors)` : ""}`);
      if (errors?.length) console.warn("CSV errors:", errors);
      setCsvOpen(false);
      setCsvText("");
      fetchItems();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setCsvLoading(false); }
  };

  const refreshFx = async () => {
    if (form.currency === "INR") return;
    setFxLoading(true);
    try {
      const r = await api.get(`/fx/rate?from_currency=${form.currency}`);
      setForm((f) => ({ ...f, fx_rate: Number(r.data.rate.toFixed(4)) }));
      toast.success(`Rate refreshed: 1 ${form.currency} = ₹${r.data.rate.toFixed(2)}`);
    } catch { toast.error("Couldn't fetch rate"); }
    finally { setFxLoading(false); }
  };

  const inrPreview = (Number(form.amount) || 0) * (Number(form.fx_rate) || 0);

  return (
    <div className="space-y-8" data-testid="income-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="tc-overline mb-2">Every rupee, accounted for</p>
          <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold tracking-tight">Income</h1>
          <p className="text-sm text-[#5a5a53] mt-2 max-w-xl">
            All the money flowing in — paid invoices, YouTube/ad-rev payouts, affiliate, royalties, bank credits. Manual, CSV, Gmail auto-sync, or AI parse-from-email.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="income-paste-email-btn" variant="outline"
            onClick={() => setPasteOpen(true)}
            className="border-[#e5e5e0] hover:bg-[#f4f1eb] rounded-md">
            <Sparkles size={14} className="mr-1.5 text-[#d95d39]" /> Paste from email
          </Button>
          <Button data-testid="income-csv-btn" variant="outline"
            onClick={() => setCsvOpen(true)}
            className="border-[#e5e5e0] hover:bg-[#f4f1eb] rounded-md">
            <Upload size={14} className="mr-1.5" /> Import CSV
          </Button>
          <Button data-testid="income-new-btn" onClick={openNew}
            className="bg-[#d95d39] hover:bg-[#c84c35] text-white rounded-md">
            <Plus size={16} className="mr-1" /> Log income
          </Button>
        </div>
      </div>

      {/* Gmail auto-sync banner */}
      <div className="tc-card p-5" data-testid="gmail-card">
        {gmail === null ? (
          <div className="text-sm text-[#5a5a53]">Loading…</div>
        ) : gmail.connected ? (
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-[#eef3ee] flex items-center justify-center">
                <Mail size={18} className="text-[#4a6b53]" strokeWidth={1.75} />
              </div>
              <div>
                <p className="text-sm font-medium text-[#0a0a0a]">
                  Gmail connected · <span className="font-mono text-[#4a6b53]">{gmail.email}</span>
                </p>
                <p className="text-[11px] text-[#5a5a53] mt-0.5">
                  {gmail.last_sync
                    ? `Last sync: ${gmail.last_sync.created || 0} new · ${gmail.last_sync.scanned || 0} scanned · ${dateFmt(gmail.last_sync.started_at)}`
                    : "Never synced — click 'Sync now' to scan the last 30 days"}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="gmail-sync-btn" onClick={syncGmail} disabled={syncing}
                className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-md">
                <Zap size={14} className={`mr-1.5 ${syncing ? "animate-pulse" : ""}`} />
                {syncing ? "Syncing…" : "Sync now"}
              </Button>
              <Button variant="outline" data-testid="gmail-disconnect-btn" onClick={disconnectGmail}
                className="border-[#e5e5e0] text-[#5a5a53] hover:bg-[#fff4f1] hover:text-[#b83a26] rounded-md">
                <Unlink size={14} className="mr-1.5" /> Disconnect
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-[#fff4ed] flex items-center justify-center">
                <Mail size={18} className="text-[#d95d39]" strokeWidth={1.75} />
              </div>
              <div>
                <p className="text-sm font-medium text-[#0a0a0a]">
                  Auto-import payouts from Gmail
                </p>
                <p className="text-[11px] text-[#5a5a53] mt-0.5 max-w-md">
                  We'll scan for YouTube, Stripe, Razorpay, Patreon, Substack &amp; PayPal payout emails — read-only access, AI extracts each automatically.
                </p>
              </div>
            </div>
            <Button data-testid="gmail-connect-btn" onClick={connectGmail}
              className="bg-[#d95d39] hover:bg-[#c84c35] text-white rounded-md">
              <Link2 size={14} className="mr-1.5" /> Connect Gmail
            </Button>
          </div>
        )}
      </div>

      {/* Auto-mode badge */}
      {queues.auto_dedup_active && (
        <div className="flex items-center gap-2 text-[11px] text-[#5a5a53]" data-testid="dedup-auto-badge">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#4a6b53]" />
          Auto-merge active · gateway ↔ bank duplicates are merged automatically (3 confirmations done)
        </div>
      )}

      {/* Dedup review queue */}
      {queues.dedup_suggestions?.length > 0 && (
        <div className="tc-card p-5 border-[#f2d9a8] bg-[#fffaf0]" data-testid="dedup-queue">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-md bg-[#d4b872] text-[#0a0a0a] flex items-center justify-center">
              <GitMerge size={16} />
            </div>
            <div className="flex-1">
              <p className="tc-overline" style={{ color: "#8c6d2a" }}>Potential duplicates</p>
              <p className="text-sm font-medium text-[#0a0a0a] mt-0.5">
                {queues.dedup_suggestions.length} settlement{queues.dedup_suggestions.length > 1 ? "s" : ""} appear to match a bank credit
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {queues.dedup_suggestions.map((s) => (
              <div key={s.id} className="bg-white border border-[#e5e5e0] rounded-md p-3 flex items-center justify-between gap-4 flex-wrap" data-testid={`dedup-${s.id}`}>
                <div className="flex-1 min-w-[280px] grid grid-cols-2 gap-3 text-xs">
                  <div className="border-r border-[#e5e5e0] pr-3">
                    <div className="text-[10px] uppercase tracking-wider text-[#a3a39b] font-bold">Gateway</div>
                    <div className="font-medium mt-1">{s.gateway?.source}</div>
                    <div className="text-[#5a5a53] mt-0.5 truncate">{s.gateway?.description}</div>
                    <div className="font-mono mt-1">{dateFmt(s.gateway?.date)} · {inr(s.gateway?.amount_inr)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#a3a39b] font-bold">Bank credit</div>
                    <div className="font-medium mt-1">{s.bank?.parser_name?.toUpperCase() || "BANK"}</div>
                    <div className="text-[#5a5a53] mt-0.5 truncate">{s.bank?.description}</div>
                    <div className="font-mono mt-1">{dateFmt(s.bank?.date)} · {inr(s.bank?.amount_inr)}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`dedup-merge-${s.id}`}
                    onClick={() => resolveDedup(s.id, "merge")}
                    className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-md h-9 px-3 text-xs">
                    <GitMerge size={12} className="mr-1" /> Merge
                  </Button>
                  <Button data-testid={`dedup-split-${s.id}`} variant="outline"
                    onClick={() => resolveDedup(s.id, "split")}
                    className="border-[#e5e5e0] hover:bg-[#fafaf8] text-[#5a5a53] rounded-md h-9 px-3 text-xs">
                    Keep both
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unclassified inbox */}
      {queues.unclassified?.length > 0 && (
        <div className="tc-card p-5 border-[#f5d5cb] bg-[#fff4f1]" data-testid="unclassified-queue">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-md bg-[#d95d39] text-white flex items-center justify-center">
              <Inbox size={16} />
            </div>
            <div className="flex-1">
              <p className="tc-overline">Unclassified · needs review</p>
              <p className="text-sm font-medium text-[#0a0a0a] mt-0.5">
                {queues.unclassified.length} entr{queues.unclassified.length > 1 ? "ies" : "y"} couldn't be auto-tagged. Pick a source — we'll learn from it.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {queues.unclassified.map((u) => (
              <UnclassifiedRow key={u.id} item={u} onClassify={classifyEntry} />
            ))}
          </div>
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#e5e5e0] border border-[#e5e5e0] rounded-md overflow-hidden">
        <Stat
          label={filter === "all" ? "Total Income (incl. GST)" : filter === "invoice" ? "Invoice income (incl. GST)" : "Other income"}
          value={inr(totalInr)}
          accent="#d95d39"
          testId="income-stat-total"
          sub={outstandingInr > 0
            ? `${inr(collectedInr)} collected · ${inr(outstandingInr)} outstanding`
            : "Earned (issuance basis) · GST-inclusive"}
        />
        <Stat label="Taxable" value={inr(taxableInr)} testId="income-stat-taxable" sub="Pre-GST taxable base" />
        <Stat
          label={filter === "all" ? "GST payable" : filter === "invoice" ? "GST on invoices" : "GST on other income"}
          value={inr(incomeGst)}
          accent="#4a6b53"
          testId="income-stat-gst"
          sub={incomeGstSubtitle}
        />
        <Stat label="Entries" value={String(filtered.length)} testId="income-stat-count" />
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-2" data-testid="income-filter-tabs">
        <FilterPill active={filter === "all"} onClick={() => setFilter("all")} testId="income-filter-all">
          All <span className="ml-1 text-[10px] text-[#a3a39b]">{items.length}</span>
        </FilterPill>
        <FilterPill active={filter === "invoice"} onClick={() => setFilter("invoice")} testId="income-filter-invoice">
          Invoices <span className="ml-1 text-[10px] text-[#a3a39b]">{invoiceCount}</span>
        </FilterPill>
        <FilterPill active={filter === "other"} onClick={() => setFilter("other")} testId="income-filter-other">
          Non-invoice <span className="ml-1 text-[10px] text-[#a3a39b]">{otherCount}</span>
        </FilterPill>
      </div>

      {/* Source breakdown */}
      {bySource.length > 0 && (
        <div className="tc-card p-6">
          <p className="tc-overline">By source</p>
          <div className="mt-4 space-y-2.5">
            {bySource.map((s) => {
              const pct = totalInr > 0 ? (s.amount / totalInr) * 100 : 0;
              return (
                <div key={s.source} data-testid={`income-source-${s.source.replace(/\s|\(|\)/g, "")}`}>
                  <div className="flex justify-between text-xs">
                    <span className="font-medium">{s.source}</span>
                    <span className="text-[#5a5a53] font-mono">{inr(s.amount)} <span className="text-[#a3a39b]">· {pct.toFixed(0)}%</span></span>
                  </div>
                  <div className="h-1.5 bg-[#f4f1eb] rounded-sm mt-1.5 overflow-hidden">
                    <div className="h-full bg-[#d95d39]" style={{ width: `${Math.max(pct, 2)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="tc-card overflow-hidden overflow-x-auto">
        {filtered.length === 0 ? (
          <div className="p-16 text-center text-sm text-[#5a5a53]" data-testid="income-empty">
            {filter === "invoice"
              ? "No invoices yet. Create one to start tracking earned income."
              : filter === "other"
              ? "No non-invoice income yet. Log a payout, paste an email, or sync your Gmail."
              : "No income yet. Create an invoice or log a payout to get started."}
          </div>
        ) : (
          <table className="w-full text-sm min-w-[780px]" data-testid="income-table">
            <thead className="bg-[#f4f1eb] text-[10px] tracking-[0.18em] uppercase text-[#5a5a53]">
              <tr>
                <th className="text-left px-6 py-3 font-bold">Date</th>
                <th className="text-left px-6 py-3 font-bold">Source</th>
                <th className="text-left px-6 py-3 font-bold">Description</th>
                <th className="text-right px-6 py-3 font-bold">Amount</th>
                <th className="text-right px-6 py-3 font-bold">In INR</th>
                <th className="text-left px-6 py-3 font-bold">GST</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={`${i.kind}-${i.id}`} className="border-t border-[#e5e5e0] hover:bg-[#fafaf8]">
                  <td className="px-6 py-3 text-[#5a5a53]">{dateFmt(i.date)}</td>
                  <td className="px-6 py-3">
                    {i.kind === "invoice" ? (
                      <div className="flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#0a0a0a] text-[#fafaf8] rounded-sm text-xs font-medium">
                          <FileText size={10} /> Invoice
                        </span>
                        {i.is_collected === false && (
                          <span
                            data-testid={`income-pending-${i.id}`}
                            className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider ${
                              i.invoice_status === "overdue"
                                ? "bg-[#ffece8] text-[#b83a26]"
                                : "bg-[#fffaf0] text-[#8c6d2a]"
                            }`}
                            title={`Status: ${i.invoice_status || "outstanding"}`}
                          >
                            {i.invoice_status === "overdue" ? "Overdue" : "Pending"}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="px-2 py-0.5 bg-[#f4f1eb] rounded-sm text-xs">{i.source}</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-[#5a5a53] max-w-xs truncate">{i.description || "—"}</td>
                  <td className="px-6 py-3 text-right font-mono">
                    {i.currency} {Number(i.amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    {i.currency !== "INR" && <span className="block text-[10px] text-[#a3a39b]">@ {i.fx_rate}</span>}
                  </td>
                  <td className="px-6 py-3 text-right font-mono font-medium">{inr(i.amount_inr)}</td>
                  <td className="px-6 py-3">
                    {i.gst_applicable
                      ? <span className="text-[#d95d39] text-xs font-bold">{i.gst_rate}% · {inr(i.gst_amount)}</span>
                      : <span className="text-[#a3a39b] text-xs">—</span>}
                  </td>
                  <td className="px-6 py-3 text-right">
                    {i.kind === "invoice" ? (
                      <button
                        data-testid={`income-invoice-link-${i.id}`}
                        onClick={() => navigate("/app/invoices")}
                        className="text-[#5a5a53] hover:text-[#0a0a0a] text-xs tracking-wider uppercase font-bold flex items-center gap-1 ml-auto"
                        title="Open in Invoices"
                      >
                        Open <ArrowRight size={12} />
                      </button>
                    ) : (
                      <div className="flex justify-end gap-3">
                        <button data-testid={`income-edit-${i.id}`} onClick={() => openEdit(i)} className="text-[#5a5a53] hover:text-[#0a0a0a]">
                          <Pencil size={15} />
                        </button>
                        <button data-testid={`income-delete-${i.id}`} onClick={() => remove(i.id)} className="text-[#5a5a53] hover:text-[#b83a26]">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* New / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-2xl">{editing ? "Edit income" : "Log income"}</DialogTitle>
            <DialogDescription className="text-xs text-[#5a5a53]">
              For income that doesn't have an invoice — payouts, royalties, ad revenue, etc.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-2 h-11 rounded-md border-[#e5e5e0]" data-testid="income-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Source</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="income-source-select" className="mt-2 h-11 rounded-md border-[#e5e5e0]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-2 h-11 rounded-md border-[#e5e5e0]" placeholder="e.g. YouTube AdSense - Jan 2026"
                data-testid="income-description" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Amount</Label>
              <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="mt-2 h-11 rounded-md border-[#e5e5e0]" data-testid="income-amount" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="income-currency-select" className="mt-2 h-11 rounded-md border-[#e5e5e0]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {form.currency !== "INR" && (
              <div className="col-span-2 bg-[#f4f1eb] rounded-md px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">FX rate (1 {form.currency} = ₹)</Label>
                  <button type="button" onClick={refreshFx} disabled={fxLoading}
                    data-testid="income-fx-refresh"
                    className="flex items-center gap-1 text-xs text-[#d95d39] hover:underline disabled:opacity-50">
                    <RefreshCw size={12} className={fxLoading ? "animate-spin" : ""} /> {fxLoading ? "Fetching..." : "Refresh"}
                  </button>
                </div>
                <Input type="number" step="0.0001" value={form.fx_rate}
                  onChange={(e) => setForm({ ...form, fx_rate: e.target.value })}
                  className="h-10 rounded-md border-[#e5e5e0] bg-white font-mono" data-testid="income-fx-rate" />
                <p className="text-[11px] text-[#5a5a53] mt-2">
                  Equivalent: <span className="font-mono font-bold text-[#0a0a0a]">{inr(inrPreview)}</span>
                </p>
              </div>
            )}
            <div className="col-span-2 flex items-center justify-between bg-[#fafaf8] border border-[#e5e5e0] rounded-md px-4 py-3">
              <div>
                <div className="text-sm font-medium">GST applicable</div>
                <div className="text-xs text-[#5a5a53]">Most platform payouts (YouTube, X, Google AdSense) are zero-rated exports — leave OFF.</div>
              </div>
              <Switch checked={form.gst_applicable}
                onCheckedChange={(v) => setForm({ ...form, gst_applicable: v })}
                data-testid="income-gst-switch" />
            </div>
            {form.gst_applicable && (
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">GST rate</Label>
                <Select value={String(form.gst_rate)} onValueChange={(v) => setForm({ ...form, gst_rate: Number(v) })}>
                  <SelectTrigger data-testid="income-gst-rate-select" className="mt-2 h-11 rounded-md border-[#e5e5e0]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {GST_RATES.map((g) => (<SelectItem key={g} value={String(g)}>{g}%</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className={`flex items-center justify-between bg-[#fafaf8] border border-[#e5e5e0] rounded-md px-4 py-3 ${form.gst_applicable ? "" : "col-span-2"}`}>
              <div>
                <div className="text-sm font-medium">Taxable income</div>
                <div className="text-xs text-[#5a5a53]">Add to gross for income-tax</div>
              </div>
              <Switch checked={form.is_taxable}
                onCheckedChange={(v) => setForm({ ...form, is_taxable: v })}
                data-testid="income-taxable-switch" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-2 rounded-md border-[#e5e5e0]" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="income-submit" onClick={submit}
              className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-md">
              {editing ? "Save changes" : "Log income"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paste from email dialog */}
      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-2xl flex items-center gap-2">
              <Sparkles size={20} className="text-[#d95d39]" /> Paste from email
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5a5a53]">
              Paste a payout email (YouTube, X, Stripe, etc.). Claude will extract the amount, currency, source and date — you'll get a chance to review before saving.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)}
            className="rounded-md border-[#e5e5e0] font-mono text-xs"
            placeholder={"Hi,\nYour YouTube payment of $245.32 for the period Jan 1 - Jan 31, 2026 has been deposited to your account on Feb 21, 2026..."}
            rows={10}
            data-testid="income-paste-textarea" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPasteOpen(false)}>Cancel</Button>
            <Button data-testid="income-paste-submit" onClick={aiParse} disabled={pasting}
              className="bg-[#d95d39] hover:bg-[#c84c35] text-white rounded-md">
              {pasting ? "Parsing..." : "Parse with AI"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV import dialog */}
      <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-2xl">Import CSV</DialogTitle>
            <DialogDescription className="text-xs text-[#5a5a53]">
              Required columns: <code className="font-mono">date, source, amount</code>. Optional: <code className="font-mono">description, currency, fx_rate, gst_applicable, gst_rate, notes</code>. Date format: YYYY-MM-DD.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={csvText} onChange={(e) => setCsvText(e.target.value)}
            className="rounded-md border-[#e5e5e0] font-mono text-xs"
            placeholder="date,source,description,amount,currency
2026-01-31,YouTube,AdSense Jan,245.32,USD
2026-02-15,Razorpay,Course payout,12500,INR"
            rows={9}
            data-testid="income-csv-textarea" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCsvOpen(false)}>Cancel</Button>
            <Button data-testid="income-csv-submit" onClick={csvImport} disabled={csvLoading}
              className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-md">
              {csvLoading ? "Importing..." : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, accent = "#0a0a0a", testId, sub }) {
  return (
    <div className="bg-white p-4 md:p-6 min-w-0" data-testid={testId}>
      <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#5a5a53] leading-tight">{label}</p>
      <p className="font-['Outfit'] text-xl md:text-2xl font-bold mt-2 font-mono break-words" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-[10px] text-[#a3a39b] mt-1.5 truncate" title={sub}>{sub}</p>}
    </div>
  );
}

function FilterPill({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3.5 py-1.5 text-xs rounded-md border transition-colors ${
        active
          ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
          : "bg-white text-[#5a5a53] border-[#e5e5e0] hover:bg-[#f4f1eb] hover:text-[#0a0a0a]"
      }`}
    >
      {children}
    </button>
  );
}

const SOURCES_FOR_CLASSIFY = [
  "YouTube", "X (Twitter)", "Instagram", "Substack", "Patreon",
  "Razorpay", "Stripe", "PayPal", "Wise", "Payoneer", "Cashfree", "Instamojo",
  "Bank credit", "Affiliate", "Speaking", "Royalty", "Other",
];

function UnclassifiedRow({ item, onClassify }) {
  const [source, setSource] = useState("");
  const [createRule, setCreateRule] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const meta = item.raw_email_meta || {};
  const senderEmail = meta.sender || "";
  const subject = meta.subject || item.description || "";

  const submit = async () => {
    if (!source) {
      toast.error("Pick a source");
      return;
    }
    setSubmitting(true);
    const isBank = source === "Bank credit";
    await onClassify(item.id, source, isBank, createRule);
    setSubmitting(false);
  };

  return (
    <div className="bg-white border border-[#e5e5e0] rounded-md p-3" data-testid={`unclassified-${item.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="font-medium text-sm text-[#0a0a0a] truncate">{subject}</div>
          <div className="text-[11px] text-[#5a5a53] mt-0.5 truncate font-mono">{senderEmail}</div>
          <div className="font-mono text-xs text-[#5a5a53] mt-1.5">
            {dateFmt(item.date)} · <span className="font-bold text-[#0a0a0a]">{inr(item.amount_inr)}</span>
            {item.currency !== "INR" && <span className="text-[#a3a39b]"> ({item.currency} {item.amount} @ {item.fx_rate})</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger data-testid={`unclassified-source-${item.id}`} className="h-9 w-44 rounded-md border-[#e5e5e0] text-xs">
              <SelectValue placeholder="Pick source..." />
            </SelectTrigger>
            <SelectContent>
              {SOURCES_FOR_CLASSIFY.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-1.5 text-[11px] text-[#5a5a53] cursor-pointer">
            <input type="checkbox" checked={createRule} onChange={(e) => setCreateRule(e.target.checked)}
              className="accent-[#d95d39]" data-testid={`unclassified-rule-${item.id}`} />
            Create rule
          </label>
          <Button data-testid={`unclassified-submit-${item.id}`} onClick={submit} disabled={!source || submitting}
            className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-md h-9 px-3 text-xs">
            <Check size={12} className="mr-1" /> Tag
          </Button>
        </div>
      </div>
    </div>
  );
}
